import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Loader2,
  Shield,
  Zap,
  Crown,
  CircleCheck,
  XCircle,
  Clock,
  Sparkles,
  Fingerprint,
  Lock,
  BadgeCheck,
  Flame,
} from "lucide-react";
import { api } from "@/lib/api";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { cn } from "@/lib/utils";
import { ManageSubscriptionCard } from "@/components/billing/ManageSubscriptionCard";

const PLAN_ICON = { free: Shield, bloq_plus: Zap, circle_pro: Crown, business: BadgeCheck };
const PLAN_ICON_WRAP = {
  free: "bg-white/5 text-bloq-muted ring-1 ring-inset ring-white/10",
  bloq_plus: "bg-gradient-to-br from-bloq-mint/25 to-bloq-blue/10 text-bloq-mint ring-1 ring-inset ring-bloq-mint/40 shadow-[0_0_24px_-6px_rgba(1,250,189,0.55)]",
  circle_pro: "bg-gradient-to-br from-bloq-purple/25 to-bloq-purple/5 text-bloq-purple ring-1 ring-inset ring-bloq-purple/40 shadow-[0_0_22px_-8px_rgba(168,85,247,0.6)]",
  business: "bg-gradient-to-br from-amber-300/25 to-amber-500/5 text-amber-300 ring-1 ring-inset ring-amber-300/40 shadow-[0_0_22px_-8px_rgba(252,211,77,0.6)]",
};
const POLL_INTERVAL = 1500;
const MAX_POLLS = 12;
const PENDING_KEY = "bloq_pending_checkout";

function ReturnBanner({ state, onDismiss }) {
  if (state === "idle") return null;
  const map = {
    checking: { icon: Loader2, spin: true, cls: "border-bloq-blue/40 bg-bloq-blue/10 text-bloq-blue", title: "Confirming your payment…", sub: "Hang tight, this only takes a moment." },
    success: { icon: CircleCheck, cls: "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint", title: "Payment confirmed", sub: "Your membership is now active." },
    incomplete: { icon: Clock, cls: "border-amber-500/40 bg-amber-500/10 text-amber-400", title: "Payment not completed", sub: "No charge was made — you can try again anytime." },
    expired: { icon: XCircle, cls: "border-amber-500/40 bg-amber-500/10 text-amber-400", title: "Checkout expired", sub: "That session expired — start again to upgrade." },
    error: { icon: XCircle, cls: "border-destructive/40 bg-destructive/10 text-destructive", title: "Something went wrong", sub: "We couldn't confirm the checkout. Please try again." },
  };
  const c = map[state];
  const Icon = c.icon;
  return (
    <div className={cn("mt-5 flex items-start gap-3 rounded-2xl border p-4", c.cls)} data-testid="checkout-return-banner">
      <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", c.spin && "animate-spin")} />
      <div className="flex-1">
        <div className="text-sm font-semibold">{c.title}</div>
        <div className="text-xs opacity-80">{c.sub}</div>
      </div>
      {state !== "checking" && (
        <button onClick={onDismiss} className="text-xs opacity-70 hover:opacity-100" data-testid="dismiss-return-banner">
          Dismiss
        </button>
      )}
    </div>
  );
}

// One-shot mint celebration when a subscription is confirmed.
function Celebration({ planName, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bloq-bg/70 backdrop-blur-sm" data-testid="celebration-overlay">
      <div className="relative flex flex-col items-center">
        <div className="celebrate-ring absolute h-24 w-24 rounded-full border-2 border-bloq-mint" aria-hidden />
        <div className="celebrate-ring absolute h-24 w-24 rounded-full border-2 border-bloq-mint [animation-delay:0.15s]" aria-hidden />
        <span className="celebrate-pop flex h-24 w-24 items-center justify-center rounded-full bloq-gradient text-bloq-bg shadow-[0_0_50px_rgba(1,250,189,0.55)]">
          <CircleCheck className="h-12 w-12" strokeWidth={2.5} />
        </span>
        <div className="celebrate-pop mt-6 text-center [animation-delay:0.1s]">
          <div className="font-heading text-2xl font-bold tracking-tight text-white">Welcome to {planName}</div>
          <div className="mt-1 text-sm text-bloq-muted">Your premium is live. Enjoy the flex. ⚡</div>
        </div>
      </div>
    </div>
  );
}

// Normalize the backend catalog into tiers, each with monthly + (optional) annual pricing.
// Forward-compatible with either shape the backend picks:
//   (A) sibling annual plans — id `${base}_annual`
//   (B) a `prices: [{ interval, usd }]` array per plan
//   (C) a convenience `annual_price_usd` field
// Until annual data exists the annual side stays null → toggle hidden (graceful degradation).
function buildTiers(catalog) {
  const siblingIds = new Set(catalog.filter((p) => p.id.endsWith("_annual")).map((p) => p.id));
  const byBase = {};
  for (const p of catalog) if (p.id.endsWith("_annual")) byBase[p.id.slice(0, -"_annual".length)] = p;

  const tiers = [];
  for (const p of catalog) {
    if (siblingIds.has(p.id)) continue; // attached to its base plan
    const monthly = { price_usd: p.price_usd, period: p.period || "month" };
    let annual = null;
    let checkoutAnnual = null;
    let checkoutInterval;

    if (Array.isArray(p.prices) && p.prices.length) {
      const m = p.prices.find((x) => (x.interval || x.period) === "month");
      const y = p.prices.find((x) => (x.interval || x.period) === "year");
      if (m) monthly.price_usd = m.usd ?? m.price_usd;
      if (y) {
        annual = { price_usd: y.usd ?? y.price_usd, period: "year" };
        checkoutAnnual = y.plan_id || y.price_id || p.id;
        checkoutInterval = "year";
      }
    }
    if (!annual && byBase[p.id]) {
      const sib = byBase[p.id];
      annual = { price_usd: sib.price_usd, period: sib.period || "year" };
      checkoutAnnual = sib.id;
    }
    if (!annual && p.annual_price_usd != null) {
      annual = { price_usd: p.annual_price_usd, period: "year" };
      checkoutAnnual = p.annual_plan_id || p.id;
      checkoutInterval = "year";
    }
    tiers.push({ ...p, monthly, annual, checkoutMonthly: p.id, checkoutAnnual, checkoutInterval });
  }
  return tiers;
}

function maxAnnualSavings(tiers) {
  let best = 0;
  for (const t of tiers) {
    if (t.annual && t.monthly.price_usd > 0 && t.annual.price_usd != null) {
      const pct = Math.round((1 - t.annual.price_usd / (t.monthly.price_usd * 12)) * 100);
      if (pct > best) best = pct;
    }
  }
  return best > 0 ? best : null;
}

function BillingToggle({ billing, setBilling, savings }) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="billing-toggle">
      <div className="inline-flex items-center rounded-full border border-white/10 bg-bloq-s1 p-1">
        {[
          ["month", "Monthly"],
          ["year", "Annual"],
        ].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setBilling(val)}
            className={cn(
              "rounded-full px-5 py-1.5 text-sm font-semibold transition-colors",
              billing === val
                ? "bloq-gradient text-bloq-bg shadow-[0_0_18px_-6px_rgba(1,250,189,0.6)]"
                : "text-bloq-muted hover:text-white",
            )}
            data-testid={`billing-toggle-${val}`}
          >
            {label}
          </button>
        ))}
      </div>
      {savings ? (
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-bloq-mint/40 bg-bloq-mint/10 px-3 py-1.5 text-xs font-bold text-bloq-mint"
          data-testid="billing-savings-chip"
        >
          <Sparkles className="h-3.5 w-3.5" /> Save up to {savings}% yearly
        </span>
      ) : null}
    </div>
  );
}

function TierCard({ plan, isCurrent, featured, billing, nativePurchases, busy, trialBusy, onUpgrade, onStartTrial, switching, pastDue }) {
  const Icon = PLAN_ICON[plan.id] || Shield;
  const isFree = plan.id === "free";
  const isPro = plan.id === "circle_pro";
  const showAnnual = billing === "year" && !!plan.annual;
  const active = showAnnual ? plan.annual : plan.monthly;
  const price = active.price_usd;
  const period = showAnnual ? "year" : active.period || "month";
  const checkoutId = showAnnual ? plan.checkoutAnnual : plan.checkoutMonthly;
  const checkoutInterval = showAnnual ? plan.checkoutInterval : undefined;
  const isContact = price == null || plan.contact === true;
  const monthlyTotal = plan.monthly?.price_usd ? plan.monthly.price_usd * 12 : null;
  const annualSave = showAnnual && monthlyTotal != null && price != null ? monthlyTotal - price : null;
  const ctaName = (plan.name || "").replace(/^BLOQ\s+/, "");
  const trialDays = plan.trial_days ?? plan.trial_period_days ?? null;
  const isTrial = trialDays > 0 && !isCurrent && !switching;
  const btnBusy = isTrial ? trialBusy : busy;
  return (
    <div
      className={cn(
        "relative flex flex-col overflow-hidden rounded-3xl border p-6 transition-transform duration-300 md:p-7",
        featured
          ? "border-bloq-mint/50 bg-gradient-to-b from-bloq-s2 to-bloq-s1 shadow-[0_0_45px_-10px_rgba(1,250,189,0.45)] md:-translate-y-2"
          : isPro
            ? "border-bloq-purple/30 bg-bloq-s1"
            : "border-white/8 bg-bloq-s1",
        isCurrent && "ring-1 ring-bloq-mint/50",
      )}
      data-testid={`plan-card-${plan.id}`}
    >
      {featured && (
        <>
          <div className="absolute inset-x-0 top-0 h-[3px] bloq-gradient" aria-hidden />
          <span className="absolute right-5 top-5 rounded-full bloq-gradient px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-bloq-bg" data-testid={`plan-popular-${plan.id}`}>
            Most popular
          </span>
        </>
      )}
      {isCurrent && (
        <span className="absolute left-5 top-5 rounded-full border border-bloq-mint/40 bg-bloq-mint/15 px-2.5 py-0.5 text-[10px] font-bold uppercase text-bloq-mint" data-testid={`plan-current-${plan.id}`}>
          Current
        </span>
      )}

      <div className={cn("mt-6 flex h-12 w-12 items-center justify-center rounded-2xl", PLAN_ICON_WRAP[plan.id])}>
        <Icon className="h-6 w-6" strokeWidth={2} />
      </div>
      <div className="mt-4 text-xl font-bold tracking-tight text-white">{plan.name}</div>
      <p className="mt-1 min-h-10 text-sm text-bloq-muted">{plan.tagline}</p>

      <div className="mt-4 flex items-end gap-1.5">
        {isContact ? (
          <span className="font-heading text-4xl font-bold text-white">Custom</span>
        ) : price === 0 ? (
          <span className="font-heading text-4xl font-bold text-white">Free</span>
        ) : (
          <>
            <span
              className={cn(
                "font-heading text-4xl font-bold",
                featured ? "bg-gradient-to-r from-bloq-mint to-bloq-blue bg-clip-text text-transparent" : "text-white",
              )}
            >
              ${price}
            </span>
            <span className="pb-1 text-sm text-bloq-muted">/ {period}</span>
          </>
        )}
      </div>
      {showAnnual && !isContact && price > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs" data-testid={`plan-annual-note-${plan.id}`}>
          {monthlyTotal != null && (
            <span className="text-bloq-muted line-through" data-testid={`plan-annual-anchor-${plan.id}`}>
              ${monthlyTotal.toFixed(2)}
            </span>
          )}
          {annualSave != null && annualSave > 0 && (
            <span className="font-bold text-bloq-mint" data-testid={`plan-annual-save-${plan.id}`}>
              Save ${annualSave.toFixed(2)}/yr
            </span>
          )}
          <span className="text-bloq-muted">· ≈ ${(price / 12).toFixed(2)}/mo</span>
        </div>
      )}

      <ul className="mt-5 flex-1 space-y-2.5">
        {(plan.perks || []).map((perk, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/85" data-testid={`plan-perk-${plan.id}-${i}`}>
            <Check className={cn("mt-0.5 h-4 w-4 shrink-0", featured ? "text-bloq-mint" : isPro ? "text-bloq-purple" : "text-bloq-muted")} />
            <span>{perk}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        {isFree ? (
          <div className="rounded-full border border-white/10 py-3 text-center text-sm text-bloq-muted" data-testid={`plan-free-note-${plan.id}`}>
            {isCurrent ? "You're on the free plan" : "Included for everyone"}
          </div>
        ) : isContact ? (
          <a
            href="mailto:sales@bloqtalk.app?subject=BLOQ%20Business%20enquiry"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 py-3 text-sm font-bold text-amber-200 transition-colors hover:bg-amber-300/20"
            data-testid={`contact-btn-${plan.id}`}
          >
            Contact sales
          </a>
        ) : nativePurchases && !isCurrent ? (
          <div className="rounded-full border border-white/10 py-3 text-center text-sm text-bloq-muted" data-testid={`native-purchase-note-${plan.id}`}>
            Manage in the mobile app
          </div>
        ) : (
          <>
            {trialDays > 0 && !isCurrent && !switching && (
              <div
                className="mb-2.5 flex items-center justify-center gap-1.5 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 py-2 text-xs font-semibold text-bloq-mint"
                data-testid={`trial-ribbon-${plan.id}`}
              >
                <Clock className="h-3.5 w-3.5" /> {trialDays}-day free trial
              </div>
            )}
            <button
              onClick={() => (isTrial ? onStartTrial() : onUpgrade(checkoutId, checkoutInterval, plan.id))}
              disabled={isCurrent || btnBusy || (switching && pastDue)}
              title={switching && pastDue ? "Fix your payment method before switching plans" : undefined}
              className={cn(
                "flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-3 text-sm font-bold transition-opacity disabled:cursor-not-allowed",
                isCurrent
                  ? "border border-white/10 text-bloq-muted disabled:opacity-100"
                  : featured
                    ? "bloq-gradient text-bloq-bg hover:opacity-90 disabled:opacity-60"
                    : "border border-white/15 text-white hover:bg-white/5 disabled:opacity-60",
              )}
              data-testid={`upgrade-btn-${plan.id}`}
            >
              {btnBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Current plan" : isTrial ? `Start free trial` : switching ? `Switch to ${ctaName}` : `Choose ${ctaName}`}
            </button>
            {switching && !isCurrent && !isContact && price != null && (
              <div className="mt-2 text-center text-xs text-bloq-muted" data-testid={`switch-note-${plan.id}`}>
                Prorated — upgrades charge the difference now, downgrades credit your next invoice
              </div>
            )}
            {trialDays > 0 && !isCurrent && !switching && !isContact && price != null && (
              <div className="mt-2 text-center text-xs text-bloq-muted" data-testid={`trial-reassure-${plan.id}`}>
                No card required · then ${price}/{period} · cancel anytime
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const TRUST_HOOKS = [
  { icon: Shield, label: "Anti-scam Shield" },
  { icon: Lock, label: "Post-quantum E2EE" },
  { icon: Fingerprint, label: "Verified wallet proofs" },
];

function timeLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `${d}d ${h}h left`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m left`;
}

// Live founding-member offer banner — reads `founding_offer` from /subscriptions/status.
// Hidden unless the backend marks it active (graceful).
function FoundingOfferBanner({ offer }) {
  if (!offer?.active) return null;
  const cap = offer.cap || 5000;
  const claimed = offer.claimed ?? (offer.remaining != null ? cap - offer.remaining : 0);
  const remaining = offer.remaining ?? Math.max(cap - claimed, 0);
  const pct = Math.min(100, Math.max(0, Math.round((claimed / cap) * 100)));
  const disc = offer.discount_pct || 50;
  const ends = timeLeft(offer.ends_at);
  return (
    <div
      className="relative mt-6 overflow-hidden rounded-3xl border border-bloq-mint/40 bg-gradient-to-br from-bloq-s2 to-bloq-s1 p-5 shadow-[0_0_45px_-14px_rgba(1,250,189,0.5)] sm:p-6"
      data-testid="founding-offer-banner"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-bloq-mint/15 blur-3xl" aria-hidden />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-bloq-mint/25 to-bloq-blue/10 text-bloq-mint ring-1 ring-inset ring-bloq-mint/40">
          <Flame className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bloq-gradient px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-bloq-bg">
              Founding offer
            </span>
            {ends && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-2 py-0.5 text-[11px] font-semibold text-bloq-mint"
                data-testid="founding-countdown"
              >
                <Clock className="h-3 w-3" /> {ends}
              </span>
            )}
          </div>
          <div className="mt-1.5 font-heading text-lg font-bold text-white sm:text-xl">
            {disc}% off BLOQ+{" "}
            <span className="bg-gradient-to-r from-bloq-mint to-bloq-blue bg-clip-text text-transparent">for life</span> + the
            exclusive Founding badge
          </div>
          <p className="mt-0.5 text-sm text-bloq-muted">
            Reserved for the first {cap.toLocaleString()} launch subscribers. Once they're gone, it's gone forever.
          </p>
          <div className="mt-3">
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/8">
              <div
                className="h-full rounded-full bloq-gradient transition-all duration-500"
                style={{ width: `${pct}%` }}
                data-testid="founding-progress"
              />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <span className="font-bold text-bloq-mint" data-testid="founding-remaining">
                {remaining.toLocaleString()} of {cap.toLocaleString()} spots left
              </span>
              <span className="text-bloq-muted">{claimed.toLocaleString()} claimed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MembershipPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["subscription"], queryFn: api.subscriptionStatus });
  const [returnState, setReturnState] = useState("idle");
  const [busyPlan, setBusyPlan] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [celebrateName, setCelebrateName] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [billing, setBilling] = useState("month");
  const pollCount = useRef(0);

  const current = data?.subscription;
  const catalog = data?.catalog || [];
  const foundingOffer = data?.founding_offer;
  const tiers = buildTiers(catalog);
  const hasAnnual = tiers.some((t) => t.annual);
  const savings = maxAnnualSavings(tiers);
  const nativePurchases = usePublicConfig()?.native_purchases === true;
  const currentPlanName = catalog.find((p) => p.id === current?.plan)?.name || current?.plan_name || "BLOQ+";

  useEffect(() => {
    const urlSession = searchParams.get("session_id");
    const cancelled = searchParams.get("checkout") === "cancel" || searchParams.get("canceled") != null;
    const pending = urlSession || localStorage.getItem(PENDING_KEY);
    const clearUrl = () => setSearchParams({}, { replace: true });

    if (cancelled) {
      localStorage.removeItem(PENDING_KEY);
      setReturnState("incomplete");
      clearUrl();
      return;
    }
    if (!pending) return;

    let stopped = false;
    setReturnState("checking");
    pollCount.current = 0;

    const finish = (state) => {
      stopped = true;
      localStorage.removeItem(PENDING_KEY);
      setReturnState(state);
      clearUrl();
    };

    const poll = async () => {
      pollCount.current += 1;
      try {
        const res = await api.subscriptionCheckoutStatus(pending);
        if (res.payment_status === "paid" || res.session_status === "complete") {
          finish("success");
          setCelebrate(true);
          await refetch();
          qc.invalidateQueries({ queryKey: ["subscription"] });
          return;
        }
        if (res.session_status === "expired") {
          finish("expired");
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          finish("error");
          return;
        }
      }
      if (stopped) return;
      if (pollCount.current >= MAX_POLLS) {
        finish("incomplete");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => {
      stopped = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (plan, interval, tierId) => {
    setBusyPlan(tierId || plan);
    try {
      // cc_21: a live auto-renewing subscription must be switched (prorated), not re-purchased.
      if (current?.manageable) return await switchPlan(plan, interval);
      const origin_url = `${window.location.origin}/app/membership`;
      const res = await api.subscriptionCheckout(plan, origin_url, interval);
      if (res.session_id) localStorage.setItem(PENDING_KEY, res.session_id);
      window.location.href = res.url;
    } catch (e) {
      const d = e?.response?.data || {};
      const code = d.code || d.detail?.code;
      if (e?.response?.status === 409 && code === "already_subscribed") return await switchPlan(plan, interval);
      toast.error(d.detail?.message || d.detail || "Couldn't start checkout");
      setBusyPlan(null);
    }
  };

  const switchPlan = async (plan, interval) => {
    try {
      const res = await api.subscriptionSwitch(plan, interval);
      const name = catalog.find((p) => p.id === plan)?.name || plan;
      const RANK = { free: 0, bloq_plus: 1, circle_pro: 2, business: 3 };
      const up = (RANK[plan] ?? 0) > (RANK[current?.plan] ?? 0);
      toast.success(up ? `Upgraded to ${name} — the prorated difference is charged now.` : `Switched to ${name} — the difference is credited on your next invoice.`);
      if (res?.url) { window.location.href = res.url; return; }
      await refetch();
      qc.invalidateQueries({ queryKey: ["subscription"] });
    } catch (e) {
      const d = e?.response?.data || {};
      const code = d.code || d.detail?.code;
      if (e?.response?.status === 409 && code === "payment_issue") toast.error("Fix your payment method before switching plans — open Manage billing.");
      else toast.error(d.detail?.message || d.detail || "Couldn't switch plans");
    } finally {
      setBusyPlan(null);
    }
  };

  // Deep-link: /app/membership?upgrade=<planId> (from the LimitMeter upsell CTA) → jump straight into
  // that plan's monthly checkout once the catalog has loaded. Runs once, then strips the params.
  const upgradeFired = useRef(false);
  useEffect(() => {
    const target = searchParams.get("upgrade");
    if (!target || upgradeFired.current || isLoading) return;
    const clear = () => {
      const n = new URLSearchParams(searchParams);
      n.delete("upgrade");
      n.delete("ref");
      setSearchParams(n, { replace: true });
    };
    if (current?.plan === target) { clear(); return; } // already on it
    // Don't send a user on an equal/higher tier into a downgrade checkout via a deep-link.
    const RANK = { free: 0, bloq_plus: 1, circle_pro: 2, business: 3 };
    if ((RANK[current?.plan] ?? 0) >= (RANK[target] ?? 0)) { clear(); return; }
    const tier = tiers.find((t) => t.id === target);
    if (!tier) { clear(); return; } // unknown plan id → ignore
    upgradeFired.current = true;
    clear();
    startCheckout(tier.checkoutMonthly, undefined, tier.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, searchParams]);

  const startTrial = async () => {
    setTrialBusy(true);
    try {
      await api.startTrial();
      setReturnState("idle");
      setCelebrateName("BLOQ+");
      setCelebrate(true);
      toast.success("Your 7-day BLOQ+ trial is live — enjoy the flex ⚡");
      await refetch();
      qc.invalidateQueries({ queryKey: ["subscription"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start your trial. Please try again.");
    } finally {
      setTrialBusy(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="membership-page">
      {celebrate && <Celebration planName={celebrateName || currentPlanName} onDone={() => { setCelebrate(false); setCelebrateName(null); }} />}

      <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/app/profile")} className="rounded-full p-1 text-bloq-muted hover:text-white" data-testid="membership-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Upgrade your BLOQ</h1>
            <p className="mt-1 text-sm text-bloq-muted">More power, more flex — trust stays free forever. Cancel anytime.</p>
          </div>
        </div>

        {/* Trust hooks */}
        <div className="mt-5 flex flex-wrap gap-2">
          {TRUST_HOOKS.map((h) => (
            <span key={h.label} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-bloq-s1 px-3 py-1.5 text-xs text-white/80">
              <h.icon className="h-3.5 w-3.5 text-bloq-mint" /> {h.label}
            </span>
          ))}
        </div>

        {hasAnnual && <BillingToggle billing={billing} setBilling={setBilling} savings={savings} />}

        <FoundingOfferBanner offer={foundingOffer} />

        <ManageSubscriptionCard sub={current} planName={currentPlanName} />

        <ReturnBanner state={returnState} onDismiss={() => setReturnState("idle")} />

        {isLoading ? (
          <div className="flex justify-center py-24 text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className={cn("mt-7 grid grid-cols-1 gap-5 lg:gap-6", tiers.length >= 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-3")}>
              {tiers.map((plan) => (
                <TierCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={current?.plan === plan.id}
                  switching={!!current?.manageable && current?.plan !== plan.id && plan.id !== "free"}
                  pastDue={current?.status === "past_due"}
                  featured={plan.id === "bloq_plus"}
                  billing={billing}
                  nativePurchases={nativePurchases}
                  busy={busyPlan === plan.id}
                  trialBusy={trialBusy}
                  onUpgrade={startCheckout}
                  onStartTrial={startTrial}
                />
              ))}
            </div>
            <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-bloq-muted">
              <Sparkles className="h-3 w-3 text-bloq-mint" /> Payments processed securely by Stripe. BLOQ Talk never sees your card details.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
